import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Film, Plus, Edit, Trash2, Upload, Search, X, ImageIcon, Sparkles, Video, Lock, Type, MonitorPlay } from 'lucide-react';
import { FeaturedManager } from '@/pages/FeaturedManager';
import { useAuth } from '@/contexts/AuthContext';
import { mediaUrl } from '../utils/mediaUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Direct browser → Cloudflare R2 upload via presigned PUT URL.
 * No chunking, no backend proxy — the browser PUTs the entire file directly to R2.
 * Works for files up to 5 GB (R2's single-PUT limit).
 *
 * onProgress signature: (pct, phase) where phase ∈ {'uploading', 'finalizing'}
 * extraInit: optional extra fields for the /upload-init call (e.g. subtitle_lang, subtitle_label)
 */
async function r2DirectUpload(movieId, file, fileType, onProgress, extraInit = {}) {
  const notify = (pct, phase = 'uploading') => { if (onProgress) onProgress(pct, phase); };

  // 1. Get presigned PUT URL from backend
  const { data: init } = await axios.post(
    `${API}/admin/movies/${movieId}/r2/upload-init`,
    {
      file_type: fileType,
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      file_size: file.size,
      ...extraInit,
    },
    { withCredentials: true }
  );

  // 2. PUT the file directly to R2 (no backend involvement)
  try {
    await axios.put(init.upload_url, file, {
      headers: { 'Content-Type': init.content_type },
      // Do NOT send cookies to R2 — would confuse the signed request
      withCredentials: false,
      timeout: 45 * 60 * 1000, // 45 min ceiling
      onUploadProgress: (e) => {
        if (e.total) notify(Math.round((e.loaded * 100) / e.total), 'uploading');
      },
    });
  } catch (err) {
    // CORS or network failure — axios throws a generic "Network Error" without
    // status code because the browser blocks it before receiving a response.
    const isCors = err.message === 'Network Error' || !err.response;
    if (isCors) {
      const origin = window.location.origin;
      throw new Error(
        `Upload blocked by CORS. Add "${origin}" to the R2 bucket's CORS AllowedOrigins ` +
        `(Cloudflare dashboard → R2 → myflix-media → Settings → CORS Policy).`
      );
    }
    throw err;
  }

  // 3. Finalize: backend verifies the object exists and updates the movie doc
  notify(99, 'finalizing');
  const { data: finalize } = await axios.post(
    `${API}/admin/movies/${movieId}/r2/upload-finalize`,
    { file_type: fileType, storage_key: init.storage_key, ...extraInit },
    { withCredentials: true }
  );
  notify(100, 'finalizing');
  return finalize;
}

const RESOLUTION_OPTIONS = [
  { key: '4k', label: '4K (2160p)' },
  { key: '1080p', label: '1080p (Full HD)' },
  { key: '720p', label: '720p (HD)' },
  { key: '480p', label: '480p (SD)' },
];

// Common language options for subtitle uploads. Admins can also type a custom code.
const SUBTITLE_LANG_PRESETS = [
  { code: 'en', label: 'English' },
  { code: 'ta', label: 'Tamil' },
  { code: 'hi', label: 'Hindi' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'zh', label: 'Chinese' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'ar', label: 'Arabic' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'ru', label: 'Russian' },
];

const AGE_RATINGS = ['G', 'PG', 'PG13', 'NC16', 'M18', 'R21'];

const emptyMovie = {
  title: '',
  original_title: '',
  synopsis: '',
  genre: [],
  language: 'English',
  cast: [],
  director: '',
  runtime: 90,
  release_year: new Date().getFullYear(),
  country: '',
  rating: 7.0,
  tags: [],
  age_rating: 'PG13',
  max_concurrent_viewers: 5,
  borrow_duration_days: 5,
  status: 'active'
};

const MovieForm = ({ movie, onClose, onSaved }) => {
  const { user: currentUser } = useAuth();
  const isMasterAdmin = currentUser?.role === 'master_admin';
  const isEdit = !!movie?.movie_id;
  const [formData, setFormData] = useState(isEdit ? { ...movie } : { ...emptyMovie });
  const [genreInput, setGenreInput] = useState((movie?.genre || []).join(', '));
  const [castInput, setCastInput] = useState((movie?.cast || []).join(', '));
  const [tagsInput, setTagsInput] = useState((movie?.tags || []).join(', '));
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  // Multi-resolution
  const [selectedResolutions, setSelectedResolutions] = useState(() => {
    const existing = (movie?.video_resolutions && typeof movie.video_resolutions === 'object')
      ? Object.keys(movie.video_resolutions) : [];
    return new Set(existing);
  });
  const [resUploadProgress, setResUploadProgress] = useState({}); // { '1080p': 42, ... }
  const [resUploading, setResUploading] = useState({}); // { '1080p': true }
  // Subtitles
  const [subtitleLang, setSubtitleLang] = useState('en');
  const [subtitleLabel, setSubtitleLabel] = useState('English');
  const [subtitleUploading, setSubtitleUploading] = useState(false);
  const [subtitleProgress, setSubtitleProgress] = useState(0);

  const inputStyle = { backgroundColor: '#0F171E', border: '1px solid #2A3F52', color: '#FFFFFF' };

  // Reload the latest movie from the server (used after uploads / deletes so
  // formData.video_resolutions and formData.subtitles show the fresh state).
  const refreshMovie = async () => {
    const currentMovieId = formData.movie_id || movie?.movie_id;
    if (!currentMovieId) return;
    try {
      const { data } = await axios.get(`${API}/movies/${currentMovieId}`, { withCredentials: true });
      setFormData(prev => ({
        ...prev,
        video_resolutions: data.video_resolutions || {},
        subtitles: data.subtitles || [],
        has_uploaded_video: !!data.has_uploaded_video,
        video_url: data.video_url || prev.video_url,
        poster_path: data.poster_path,
        banner_path: data.banner_path,
      }));
    } catch (e) { /* soft-fail */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        genre: genreInput.split(',').map(s => s.trim()).filter(Boolean),
        cast: castInput.split(',').map(s => s.trim()).filter(Boolean),
        tags: tagsInput.split(',').map(s => s.trim()).filter(Boolean),
        runtime: parseInt(formData.runtime),
        release_year: parseInt(formData.release_year),
        rating: parseFloat(formData.rating),
        max_concurrent_viewers: parseInt(formData.max_concurrent_viewers),
        borrow_duration_days: parseInt(formData.borrow_duration_days)
      };

      let savedMovieId;
      if (isEdit || formData.movie_id) {
        const targetId = isEdit ? movie.movie_id : formData.movie_id;
        await axios.put(`${API}/admin/movies/${targetId}`, payload, { withCredentials: true });
        savedMovieId = targetId;
        toast.success('Movie updated');
      } else {
        const { data } = await axios.post(`${API}/admin/movies`, payload, { withCredentials: true });
        savedMovieId = data.movie_id;
        toast.success('Movie created');
      }
      onSaved();
      if (!isEdit) {
        // Keep form open for image + video uploads; subsequent saves are PUTs
        setFormData({ ...formData, movie_id: savedMovieId });
      } else {
        onClose();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save movie');
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageUpload = async (type, file) => {
    if (!file) return;
    const currentMovieId = formData.movie_id || movie?.movie_id;
    if (!currentMovieId) {
      toast.error('Save movie details first, then upload images');
      return;
    }
    setUploading({ ...uploading, [type]: true });
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(
        `${API}/admin/movies/${currentMovieId}/upload/${type}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`${type} uploaded`);
      const fieldName = `${type}_path`;
      setFormData(prev => ({ ...prev, [fieldName]: data.path }));
      onSaved();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Upload failed');
    } finally {
      setUploading({ ...uploading, [type]: false });
    }
  };

  const handleVideoUpload = async (file) => {
    if (!file) return;
    const currentMovieId = formData.movie_id || movie?.movie_id;
    if (!currentMovieId) {
      toast.error('Save movie details first, then upload video');
      return;
    }
    if (file.size > 5 * 1024 * 1024 * 1024) {
      toast.error('Video exceeds 5 GB limit');
      return;
    }
    setUploading(prev => ({ ...prev, video: true }));
    setUploadProgress(0);
    try {
      await r2DirectUpload(currentMovieId, file, 'video', (pct) => setUploadProgress(pct));
      toast.success('Video uploaded — members can now stream this movie');
      setFormData(prev => ({ ...prev, video_url: 'uploaded', has_uploaded_video: true }));
      onSaved();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      toast.error(typeof detail === 'string' ? detail : 'Video upload failed');
    } finally {
      setUploading(prev => ({ ...prev, video: false }));
      setUploadProgress(0);
    }
  };

  const deleteAsset = async (params) => {
    const currentMovieId = formData.movie_id || movie?.movie_id;
    if (!currentMovieId) return;
    await axios.delete(`${API}/admin/movies/${currentMovieId}/asset`, {
      params,
      withCredentials: true,
    });
  };

  const handleRemovePoster = async (posterType) => {
    // posterType: 'poster_desktop' (16:9) | 'poster_mobile' (9:16)
    const label = posterType === 'poster_desktop' ? 'desktop banner (16:9)' : 'mobile poster (9:16)';
    if (!window.confirm(`Remove the ${label}?\nThis permanently deletes the image from Cloudflare R2.`)) return;
    setUploading(prev => ({ ...prev, [`remove_${posterType}`]: true }));
    try {
      await deleteAsset({ asset_type: posterType });
      toast.success(`${label} removed`);
      setFormData(prev => ({
        ...prev,
        ...(posterType === 'poster_desktop' ? { banner_path: null, poster_desktop_path: null } : { poster_path: null, poster_mobile_path: null })
      }));
      onSaved();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      toast.error(typeof detail === 'string' ? detail : 'Failed to remove poster');
    } finally {
      setUploading(prev => ({ ...prev, [`remove_${posterType}`]: false }));
    }
  };

  const handleResolutionUpload = async (res, file) => {
    if (!file) return;
    const currentMovieId = formData.movie_id || movie?.movie_id;
    if (!currentMovieId) {
      toast.error('Save movie details first, then upload video files');
      return;
    }
    if (file.size > 5 * 1024 * 1024 * 1024) {
      toast.error('Video exceeds 5 GB limit');
      return;
    }
    setResUploading(prev => ({ ...prev, [res]: true }));
    setResUploadProgress(prev => ({ ...prev, [res]: 0 }));
    try {
      await r2DirectUpload(currentMovieId, file, `video_${res}`, (pct) => {
        setResUploadProgress(prev => ({ ...prev, [res]: pct }));
      });
      toast.success(`${res.toUpperCase()} uploaded`);
      await refreshMovie();
      onSaved();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      toast.error(typeof detail === 'string' ? detail : `${res} upload failed`);
    } finally {
      setResUploading(prev => ({ ...prev, [res]: false }));
      setResUploadProgress(prev => ({ ...prev, [res]: 0 }));
    }
  };

  const handleRemoveResolution = async (res) => {
    if (!window.confirm(`Delete the ${res.toUpperCase()} variant from Cloudflare R2? This cannot be undone.`)) return;
    try {
      await deleteAsset({ asset_type: 'resolution', res });
      toast.success(`${res.toUpperCase()} removed`);
      await refreshMovie();
      setSelectedResolutions(prev => {
        const next = new Set(prev);
        next.delete(res);
        return next;
      });
      onSaved();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      toast.error(typeof detail === 'string' ? detail : 'Failed to remove resolution');
    }
  };

  const handleSubtitleUpload = async (file) => {
    if (!file) return;
    const currentMovieId = formData.movie_id || movie?.movie_id;
    if (!currentMovieId) {
      toast.error('Save movie details first, then upload subtitles');
      return;
    }
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['srt', 'vtt'].includes(ext)) {
      toast.error('Subtitle files must be .srt or .vtt');
      return;
    }
    const lang = (subtitleLang || '').trim().toLowerCase();
    const label = (subtitleLabel || '').trim() || lang;
    if (!lang || !/^[a-z0-9-]{1,12}$/.test(lang)) {
      toast.error('Language code must be 1-12 lowercase letters/numbers (e.g. en, ta, pt-br)');
      return;
    }
    setSubtitleUploading(true);
    setSubtitleProgress(0);
    try {
      await r2DirectUpload(
        currentMovieId,
        file,
        'subtitle',
        (pct) => setSubtitleProgress(pct),
        { subtitle_lang: lang, subtitle_label: label }
      );
      toast.success(`Subtitle "${label}" uploaded`);
      await refreshMovie();
      onSaved();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      toast.error(typeof detail === 'string' ? detail : 'Subtitle upload failed');
    } finally {
      setSubtitleUploading(false);
      setSubtitleProgress(0);
    }
  };

  const handleRemoveSubtitle = async (lang) => {
    if (!window.confirm(`Remove the "${lang}" subtitle track from Cloudflare R2?`)) return;
    try {
      await deleteAsset({ asset_type: 'subtitle', lang });
      toast.success('Subtitle removed');
      await refreshMovie();
      onSaved();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      toast.error(typeof detail === 'string' ? detail : 'Failed to remove subtitle');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}>
      <div className="w-full max-w-3xl rounded-2xl max-h-[92vh] overflow-y-auto" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
        <div className="sticky top-0 z-10 flex items-center justify-between p-6" style={{ backgroundColor: '#1A2632', borderBottom: '1px solid #2A3F52' }}>
          <div className="flex items-center gap-3">
            <Film size={24} style={{ color: '#3B82F6' }} />
            <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>{isEdit ? 'Edit Movie' : 'Add New Movie'}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg" style={{ backgroundColor: '#2A3F52' }}>
            <X size={20} color="white" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Step banner — shows in Add mode before movie is saved */}
          {!isEdit && !formData.movie_id && (
            <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(59, 130, 246, 0.10)', border: '1px solid #3B82F6' }} data-testid="add-movie-steps-banner">
              <p className="text-sm font-semibold mb-2" style={{ color: '#3B82F6' }}>How to add a movie</p>
              <ol className="text-xs space-y-1" style={{ color: '#DDDDDD' }}>
                <li><span className="font-bold" style={{ color: '#3B82F6' }}>1.</span> Fill in the details below and click <span className="font-bold">Create Movie</span></li>
                <li><span className="font-bold" style={{ color: '#3B82F6' }}>2.</span> This screen will unlock <span className="font-bold">Poster</span> and <span className="font-bold">Video File</span> upload sections</li>
                <li><span className="font-bold" style={{ color: '#3B82F6' }}>3.</span> Upload the movie file (MP4/WebM/MOV) — members will stream this file when they watch</li>
              </ol>
            </div>
          )}

          {/* Image Uploads (Desktop 16:9 and Mobile 9:16) */}
          {(isEdit || formData.movie_id) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#DDDDDD' }}>
                  Desktop Poster (16:9)
                </label>
                <div className="relative">
                  <label className="block relative cursor-pointer group rounded-lg overflow-hidden" style={{
                    backgroundColor: '#0F171E',
                    border: '2px dashed #2A3F52',
                    aspectRatio: '16/9'
                  }}>
                    {formData.poster_desktop_path || formData.banner_path ? (
                      <img src={mediaUrl(formData.poster_desktop_path || formData.banner_path, API)}
                        alt="Desktop poster" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center">
                        <ImageIcon size={32} style={{ color: '#8197A4' }} />
                        <p className="text-xs mt-2" style={{ color: '#8197A4' }}>16:9 Desktop Banner</p>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center transition-all">
                      <Upload size={24} className="opacity-0 group-hover:opacity-100 transition-opacity" color="white" />
                    </div>
                    <input type="file" accept="image/*" onChange={(e) => handleImageUpload('poster_desktop', e.target.files[0])}
                      className="hidden" data-testid="upload-poster-desktop" />
                  </label>
                  {(formData.poster_desktop_path || formData.banner_path) && (
                    <button
                      type="button"
                      onClick={() => handleRemovePoster('poster_desktop')}
                      disabled={uploading.remove_poster_desktop}
                      className="absolute top-2 right-2 p-2 rounded-full transition-all hover:scale-110"
                      style={{ backgroundColor: 'rgba(220, 38, 38, 0.9)', color: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.5)' }}
                      title="Remove desktop banner (deletes from R2)"
                      data-testid="remove-poster-desktop"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                {uploading.poster_desktop && <p className="text-xs mt-1" style={{ color: '#3B82F6' }}>Uploading...</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#DDDDDD' }}>
                  Mobile Poster (9:16)
                </label>
                <div className="relative mx-auto" style={{ maxWidth: '160px' }}>
                  <label className="block relative cursor-pointer group rounded-lg overflow-hidden" style={{
                    backgroundColor: '#0F171E',
                    border: '2px dashed #2A3F52',
                    aspectRatio: '9/16'
                  }}>
                    {formData.poster_mobile_path || formData.poster_path ? (
                      <img src={mediaUrl(formData.poster_mobile_path || formData.poster_path, API)}
                        alt="Mobile poster" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center p-2">
                        <ImageIcon size={28} style={{ color: '#8197A4' }} />
                        <p className="text-xs mt-2 text-center" style={{ color: '#8197A4' }}>9:16 Mobile Poster</p>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center transition-all">
                      <Upload size={20} className="opacity-0 group-hover:opacity-100 transition-opacity" color="white" />
                    </div>
                    <input type="file" accept="image/*" onChange={(e) => handleImageUpload('poster_mobile', e.target.files[0])}
                      className="hidden" data-testid="upload-poster-mobile" />
                  </label>
                  {(formData.poster_mobile_path || formData.poster_path) && (
                    <button
                      type="button"
                      onClick={() => handleRemovePoster('poster_mobile')}
                      disabled={uploading.remove_poster_mobile}
                      className="absolute top-2 right-2 p-2 rounded-full transition-all hover:scale-110"
                      style={{ backgroundColor: 'rgba(220, 38, 38, 0.9)', color: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.5)' }}
                      title="Remove mobile poster (deletes from R2)"
                      data-testid="remove-poster-mobile"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                {uploading.poster_mobile && <p className="text-xs mt-1" style={{ color: '#3B82F6' }}>Uploading...</p>}
              </div>
            </div>
          )}

          {/* Video Upload — MASTER ADMINS ONLY */}
          {(isEdit || formData.movie_id) && isMasterAdmin && (
            <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: '#0F171E', border: '1px solid #2A3F52' }}>
              <div className="flex items-center gap-2 mb-2">
                <Upload size={18} style={{ color: '#10B981' }} />
                <label className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>Movie Video File</label>
                {formData.has_uploaded_video && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)', color: '#10B981' }}>
                    UPLOADED
                  </span>
                )}
              </div>
              <p className="text-xs mb-3" style={{ color: '#8197A4' }}>
                Upload the full movie file (MP4/WebM/MOV, up to 5 GB). Uploads are automatically split into 5 MB chunks so large files stream in reliably. Members with an active borrow will stream this file.
              </p>
              <label className="block cursor-pointer">
                <div
                  className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors"
                  style={{
                    backgroundColor: uploading.video ? '#2A3F52' : 'rgba(16, 185, 129, 0.10)',
                    border: '1px dashed #10B981',
                    color: '#10B981'
                  }}
                >
                  <Upload size={16} />
                  <span className="text-sm font-medium">
                    {uploading.video
                      ? `Uploading video... ${uploadProgress}%`
                      : (formData.has_uploaded_video ? 'Replace video file' : 'Choose video file (MP4)')}
                  </span>
                </div>
                <input
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime"
                  onChange={(e) => handleVideoUpload(e.target.files[0])}
                  className="hidden"
                  data-testid="upload-movie-video"
                  disabled={uploading.video}
                />
              </label>
              {uploading.video && (
                <div className="w-full h-2 mt-3 rounded-full overflow-hidden" style={{ backgroundColor: '#2A3F52' }}>
                  <div
                    className="h-full transition-all"
                    style={{ width: `${uploadProgress}%`, backgroundColor: '#10B981' }}
                    data-testid="video-upload-progress"
                  />
                </div>
              )}
              {formData.video_url && !uploading.video && (
                <p className="text-[11px] mt-2 font-mono truncate" style={{ color: '#8197A4' }} title={formData.video_url}>
                  Current: {formData.video_url}
                </p>
              )}
            </div>
          )}

          {/* Multi-resolution uploads — MASTER ADMIN ONLY */}
          {(isEdit || formData.movie_id) && isMasterAdmin && (
            <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: '#0F171E', border: '1px solid #2A3F52' }} data-testid="resolution-section">
              <div className="flex items-center gap-2 mb-2">
                <MonitorPlay size={18} style={{ color: '#3B82F6' }} />
                <label className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>Multi-Resolution Uploads</label>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#3B82F6' }}>
                  OPTIONAL
                </span>
              </div>
              <p className="text-xs mb-3" style={{ color: '#8197A4' }}>
                Tick the qualities you want to offer, then upload a separate MP4 for each. The player shows a Quality selector; the highest-quality file plays by default. Each file is stored independently on Cloudflare R2.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                {RESOLUTION_OPTIONS.map(opt => {
                  const uploaded = !!(formData.video_resolutions || {})[opt.key];
                  const checked = selectedResolutions.has(opt.key) || uploaded;
                  return (
                    <label
                      key={opt.key}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer"
                      style={{
                        backgroundColor: checked ? 'rgba(59, 130, 246, 0.12)' : '#1A2632',
                        border: `1px solid ${checked ? '#3B82F6' : '#2A3F52'}`,
                      }}
                      data-testid={`resolution-toggle-${opt.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={uploaded}
                        onChange={(e) => {
                          setSelectedResolutions(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(opt.key); else next.delete(opt.key);
                            return next;
                          });
                        }}
                      />
                      <span className="text-xs font-semibold" style={{ color: checked ? '#3B82F6' : '#DDDDDD' }}>{opt.label}</span>
                    </label>
                  );
                })}
              </div>

              <div className="space-y-2">
                {RESOLUTION_OPTIONS.filter(opt => selectedResolutions.has(opt.key) || (formData.video_resolutions || {})[opt.key]).map(opt => {
                  const uploaded = !!(formData.video_resolutions || {})[opt.key];
                  const isUploadingThis = !!resUploading[opt.key];
                  const pct = resUploadProgress[opt.key] || 0;
                  return (
                    <div key={opt.key} className="p-3 rounded-lg" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <Video size={14} style={{ color: uploaded ? '#10B981' : '#8197A4' }} />
                          <span className="text-xs font-semibold" style={{ color: '#FFFFFF' }}>{opt.label}</span>
                          {uploaded && (
                            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold" style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)', color: '#10B981' }}>
                              UPLOADED
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="cursor-pointer">
                            <span
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] font-semibold"
                              style={{
                                backgroundColor: isUploadingThis ? '#2A3F52' : 'rgba(59, 130, 246, 0.12)',
                                border: '1px solid #3B82F6',
                                color: '#3B82F6',
                                opacity: isUploadingThis ? 0.7 : 1,
                              }}
                            >
                              <Upload size={12} />
                              {isUploadingThis ? `Uploading ${pct}%` : (uploaded ? 'Replace' : 'Choose file')}
                            </span>
                            <input
                              type="file"
                              accept="video/mp4,video/webm,video/quicktime,video/x-m4v"
                              className="hidden"
                              disabled={isUploadingThis}
                              onChange={(e) => handleResolutionUpload(opt.key, e.target.files[0])}
                              data-testid={`upload-resolution-${opt.key}`}
                            />
                          </label>
                          {uploaded && (
                            <button
                              type="button"
                              onClick={() => handleRemoveResolution(opt.key)}
                              className="p-1.5 rounded transition-colors"
                              style={{ backgroundColor: 'rgba(220, 38, 38, 0.15)', color: '#EF4444' }}
                              title={`Delete ${opt.label} from R2`}
                              data-testid={`remove-resolution-${opt.key}`}
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      </div>
                      {isUploadingThis && (
                        <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#2A3F52' }}>
                          <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: '#3B82F6' }} />
                        </div>
                      )}
                    </div>
                  );
                })}
                {selectedResolutions.size === 0 && Object.keys(formData.video_resolutions || {}).length === 0 && (
                  <p className="text-xs italic" style={{ color: '#8197A4' }}>
                    Tick any resolution above to enable its upload slot. The single &quot;Movie Video File&quot; above still works as a default when no resolutions are uploaded.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Subtitles (multi-language SRT / VTT) — MASTER ADMIN ONLY */}
          {(isEdit || formData.movie_id) && isMasterAdmin && (
            <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: '#0F171E', border: '1px solid #2A3F52' }} data-testid="subtitles-section">
              <div className="flex items-center gap-2 mb-2">
                <Type size={18} style={{ color: '#F0C14B' }} />
                <label className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>Subtitles</label>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: 'rgba(240, 193, 75, 0.15)', color: '#F0C14B' }}>
                  {(formData.subtitles || []).length} TRACK{(formData.subtitles || []).length === 1 ? '' : 'S'}
                </span>
              </div>
              <p className="text-xs mb-3" style={{ color: '#8197A4' }}>
                Upload one SRT (.srt) or WebVTT (.vtt) file per language. Players show a menu so viewers can pick a track. SRT files are converted to VTT on the fly.
              </p>

              {/* Existing subtitle tracks */}
              {(formData.subtitles || []).length > 0 && (
                <div className="space-y-2 mb-3">
                  {(formData.subtitles || []).map(s => (
                    <div key={s.lang} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }} data-testid={`subtitle-track-${s.lang}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Type size={14} style={{ color: '#F0C14B' }} />
                        <span className="text-xs font-semibold uppercase" style={{ color: '#F0C14B' }}>{s.lang}</span>
                        <span className="text-xs truncate" style={{ color: '#DDDDDD' }}>{s.label}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveSubtitle(s.lang)}
                        className="p-1.5 rounded"
                        style={{ backgroundColor: 'rgba(220, 38, 38, 0.15)', color: '#EF4444' }}
                        title={`Remove ${s.label}`}
                        data-testid={`remove-subtitle-${s.lang}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload new subtitle */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                <div>
                  <label className="block text-[11px] mb-1" style={{ color: '#8197A4' }}>Language code</label>
                  <input
                    list="subtitle-lang-presets"
                    type="text"
                    value={subtitleLang}
                    onChange={(e) => {
                      const v = e.target.value.toLowerCase();
                      setSubtitleLang(v);
                      const preset = SUBTITLE_LANG_PRESETS.find(p => p.code === v);
                      if (preset) setSubtitleLabel(preset.label);
                    }}
                    className="w-full px-3 py-2 rounded-lg outline-none text-sm"
                    style={inputStyle}
                    placeholder="en"
                    data-testid="subtitle-lang-input"
                  />
                  <datalist id="subtitle-lang-presets">
                    {SUBTITLE_LANG_PRESETS.map(p => (
                      <option key={p.code} value={p.code}>{p.label}</option>
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-[11px] mb-1" style={{ color: '#8197A4' }}>Label (shown in player)</label>
                  <input
                    type="text"
                    value={subtitleLabel}
                    onChange={(e) => setSubtitleLabel(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg outline-none text-sm"
                    style={inputStyle}
                    placeholder="English"
                    data-testid="subtitle-label-input"
                  />
                </div>
              </div>
              <label className="block cursor-pointer">
                <div
                  className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors"
                  style={{
                    backgroundColor: subtitleUploading ? '#2A3F52' : 'rgba(240, 193, 75, 0.10)',
                    border: '1px dashed #F0C14B',
                    color: '#F0C14B'
                  }}
                >
                  <Upload size={16} />
                  <span className="text-sm font-medium">
                    {subtitleUploading ? `Uploading subtitle... ${subtitleProgress}%` : 'Choose subtitle file (.srt / .vtt)'}
                  </span>
                </div>
                <input
                  type="file"
                  accept=".srt,.vtt,text/plain"
                  onChange={(e) => handleSubtitleUpload(e.target.files[0])}
                  className="hidden"
                  disabled={subtitleUploading}
                  data-testid="upload-subtitle"
                />
              </label>
              {subtitleUploading && (
                <div className="w-full h-2 mt-3 rounded-full overflow-hidden" style={{ backgroundColor: '#2A3F52' }}>
                  <div className="h-full transition-all" style={{ width: `${subtitleProgress}%`, backgroundColor: '#F0C14B' }} />
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: '#DDDDDD' }}>Title *</label>
              <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} required data-testid="movie-title-input" />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: '#DDDDDD' }}>Original Title</label>
              <input type="text" value={formData.original_title || ''} onChange={(e) => setFormData({ ...formData, original_title: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: '#DDDDDD' }}>Synopsis *</label>
              <textarea value={formData.synopsis} onChange={(e) => setFormData({ ...formData, synopsis: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} required rows="4" data-testid="movie-synopsis-input" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#DDDDDD' }}>Runtime (min) *</label>
                <input type="number" value={formData.runtime} onChange={(e) => setFormData({ ...formData, runtime: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} required min="1" data-testid="movie-runtime-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#DDDDDD' }}>Year *</label>
                <input type="number" value={formData.release_year} onChange={(e) => setFormData({ ...formData, release_year: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} required min="1900" max="2100" data-testid="movie-year-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#DDDDDD' }}>Rating</label>
                <input type="number" step="0.1" min="0" max="10" value={formData.rating} onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#DDDDDD' }}>Age Rating *</label>
                <select value={formData.age_rating} onChange={(e) => setFormData({ ...formData, age_rating: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} data-testid="movie-age-rating-input">
                  {AGE_RATINGS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#DDDDDD' }}>Genres (comma-separated)</label>
                <input type="text" value={genreInput} onChange={(e) => setGenreInput(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle}
                  placeholder="Action, Thriller" data-testid="movie-genre-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#DDDDDD' }}>Language</label>
                <input type="text" value={formData.language} onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#DDDDDD' }}>Director</label>
                <input type="text" value={formData.director} onChange={(e) => setFormData({ ...formData, director: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#DDDDDD' }}>Country</label>
                <input type="text" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: '#DDDDDD' }}>Cast (comma-separated)</label>
              <input type="text" value={castInput} onChange={(e) => setCastInput(e.target.value)}
                className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} placeholder="Actor 1, Actor 2" />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: '#DDDDDD' }}>Tags (comma-separated)</label>
              <input type="text" value={tagsInput} onChange={(e) => setTagsInput(e.target.value)}
                className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} placeholder="mind-bending, epic" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#DDDDDD' }}>Max Concurrent Viewers</label>
                <input type="number" min="1" value={formData.max_concurrent_viewers}
                  onChange={(e) => setFormData({ ...formData, max_concurrent_viewers: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#DDDDDD' }}>Borrow Duration (days)</label>
                <input type="number" min="1" value={formData.borrow_duration_days}
                  onChange={(e) => setFormData({ ...formData, borrow_duration_days: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: '#DDDDDD' }}>Status</label>
              <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle}>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>

            <div className="flex gap-3 pt-4" style={{ borderTop: '1px solid #2A3F52' }}>
              <button type="button" onClick={onClose} className="flex-1 py-3 rounded-lg font-semibold"
                style={{ backgroundColor: '#2A3F52', color: '#FFFFFF' }}>
                Cancel
              </button>
              <button type="submit" disabled={submitting} className="flex-1 py-3 rounded-lg font-semibold"
                style={{ backgroundColor: '#1E3A8A', color: 'white' }} data-testid="save-movie-button">
                {submitting ? 'Saving...' : (isEdit ? 'Update Movie' : 'Create Movie')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export const ManageMoviesPage = () => {
  const { user: currentUser } = useAuth();
  const isMasterAdmin = currentUser?.role === 'master_admin';
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editMovie, setEditMovie] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [rowUploading, setRowUploading] = useState({});
  const [rowProgress, setRowProgress] = useState({});
  const [rowPhase, setRowPhase] = useState({}); // 'uploading' | 'processing'
  const rowFileInputRefs = useRef({});

  const location = useLocation();
  const navigate = useNavigate();
  const tabFromUrl = new URLSearchParams(location.search).get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl === 'featured' ? 'featured' : 'movies');

  useEffect(() => {
    fetchMovies();
  }, []);

  useEffect(() => {
    const t = new URLSearchParams(location.search).get('tab');
    if (t === 'featured' && activeTab !== 'featured') setActiveTab('featured');
    if ((!t || t === 'movies') && activeTab !== 'movies') setActiveTab('movies');
  }, [location.search, activeTab]);

  const changeTab = (t) => {
    setActiveTab(t);
    navigate(t === 'featured' ? '/manage-movies?tab=featured' : '/manage-movies', { replace: true });
  };

  const fetchMovies = async () => {
    try {
      const { data } = await axios.get(`${API}/movies`, { withCredentials: true });
      setMovies(data);
    } catch (error) {
      toast.error('Failed to load movies');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (movieId, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/movies/${movieId}`, { withCredentials: true });
      toast.success('Movie deleted');
      fetchMovies();
    } catch (error) {
      toast.error('Failed to delete movie');
    }
  };

  const handleRowVideoUpload = async (movieId, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024 * 1024) {
      toast.error('Video exceeds 5 GB limit');
      return;
    }
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['mp4', 'webm', 'mov', 'm4v'].includes(ext)) {
      toast.error('Please choose an MP4, WebM or MOV file');
      return;
    }
    setRowUploading(prev => ({ ...prev, [movieId]: true }));
    setRowProgress(prev => ({ ...prev, [movieId]: 0 }));
    try {
      await r2DirectUpload(
        movieId,
        file,
        'video',
        (pct, phase) => {
          setRowProgress(prev => ({ ...prev, [movieId]: pct }));
          setRowPhase(prev => ({ ...prev, [movieId]: phase }));
        }
      );
      toast.success('Video uploaded — members can now stream this movie');
      fetchMovies();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      toast.error(typeof detail === 'string' ? detail : 'Video upload failed');
    } finally {
      setRowUploading(prev => ({ ...prev, [movieId]: false }));
      setRowProgress(prev => ({ ...prev, [movieId]: 0 }));
      setRowPhase(prev => ({ ...prev, [movieId]: null }));
      if (rowFileInputRefs.current[movieId]) rowFileInputRefs.current[movieId].value = '';
    }
  };

  const filtered = movies.filter(m =>
    !search || m.title.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0F171E' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: '#3B82F6' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 px-4 sm:px-8 md:px-12 lg:px-16 pb-12" style={{ backgroundColor: '#0F171E' }} data-testid="manage-movies-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Film size={32} style={{ color: '#3B82F6' }} />
          <h1 className="text-4xl font-bold" style={{ color: '#FFFFFF' }}>Manage Movies</h1>
        </div>
        <p className="mb-6" style={{ color: '#8197A4' }}>Add, edit, and curate the movie library and featured picks</p>

        <div className="mb-6 flex gap-3 flex-wrap">
          <button
            onClick={() => changeTab('movies')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors duration-200"
            style={{
              backgroundColor: activeTab === 'movies' ? '#1E3A8A' : '#1A2632',
              color: activeTab === 'movies' ? 'white' : '#8197A4',
              border: `1px solid ${activeTab === 'movies' ? '#1E3A8A' : '#2A3F52'}`
            }}
            data-testid="manage-tab-movies"
          >
            <Film size={16} /> Movies
          </button>
          <button
            onClick={() => changeTab('featured')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors duration-200"
            style={{
              backgroundColor: activeTab === 'featured' ? '#1E3A8A' : '#1A2632',
              color: activeTab === 'featured' ? 'white' : '#8197A4',
              border: `1px solid ${activeTab === 'featured' ? '#1E3A8A' : '#2A3F52'}`
            }}
            data-testid="manage-tab-featured"
          >
            <Sparkles size={16} /> Featured
          </button>
        </div>

        {activeTab === 'featured' ? (
          <FeaturedManager />
        ) : (
          <>
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <p style={{ color: '#8197A4' }}>{movies.length} total</p>
          <button onClick={() => { setEditMovie(null); setShowForm(true); }}
            className="flex items-center gap-2 px-5 py-3 rounded-lg font-bold hover:scale-105"
            style={{ backgroundColor: '#1E3A8A', color: 'white', boxShadow: '0 4px 15px rgba(30, 58, 138, 0.35)' }}
            data-testid="add-new-movie-button">
            <Plus size={20} /> Add New Movie
          </button>
        </div>

        <div className="relative mb-6">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search movies by title..."
            className="w-full pl-12 pr-4 py-3 rounded-lg outline-none"
            style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52', color: '#FFFFFF' }}
            data-testid="manage-movies-search" />
          <Search size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2" style={{ color: '#8197A4' }} />
        </div>

        <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: '#0F171E' }}>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: '#8197A4' }}>Poster</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: '#8197A4' }}>Title</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: '#8197A4' }}>Year</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: '#8197A4' }}>Duration</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: '#8197A4' }}>Age Rating</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: '#8197A4' }}>Status</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: '#8197A4' }}>Video</th>
                  <th className="text-right py-3 px-4 font-semibold" style={{ color: '#8197A4' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(movie => (
                  <tr key={movie.movie_id} style={{ borderTop: '1px solid #2A3F52' }} data-testid={`manage-movie-row-${movie.movie_id}`}>
                    <td className="py-3 px-4">
                      <img src={mediaUrl(movie.poster_path, API) || `https://picsum.photos/seed/${movie.movie_id}/40/60`}
                        alt={movie.title} className="w-10 h-14 object-cover rounded" />
                    </td>
                    <td className="py-3 px-4">
                      <p className="font-semibold" style={{ color: '#FFFFFF' }}>{movie.title}</p>
                      {movie.director && <p className="text-xs" style={{ color: '#8197A4' }}>{movie.director}</p>}
                    </td>
                    <td className="py-3 px-4" style={{ color: '#DDDDDD' }}>{movie.release_year}</td>
                    <td className="py-3 px-4" style={{ color: '#DDDDDD' }}>{movie.runtime} min</td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 rounded text-xs font-semibold"
                        style={{ backgroundColor: '#1E3A8A', color: 'white' }}>
                        {movie.age_rating}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 rounded-full text-xs font-semibold"
                        style={{
                          backgroundColor: movie.status === 'active' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(129, 151, 164, 0.15)',
                          color: movie.status === 'active' ? '#10B981' : '#8197A4'
                        }}>
                        {movie.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {movie.has_uploaded_video ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold"
                          style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)', color: '#10B981' }}
                          data-testid={`video-status-${movie.movie_id}`}
                          title="A movie file is uploaded and ready to stream">
                          <Video size={12} /> Uploaded
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                          style={{ backgroundColor: 'rgba(129, 151, 164, 0.15)', color: '#8197A4' }}
                          data-testid={`video-status-${movie.movie_id}`}
                          title="No movie file uploaded — will play the sample video until you upload">
                          <Video size={12} /> Sample
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-end gap-2 items-center flex-wrap">
                        {/* Upload video (or replace) — MASTER ADMIN ONLY */}
                        {isMasterAdmin ? (
                          <label
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all ${rowUploading[movie.movie_id] ? 'opacity-70 cursor-wait' : 'hover:scale-105'}`}
                            style={{
                              backgroundColor: movie.has_uploaded_video ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.10)',
                              color: '#10B981',
                              border: '1px solid #10B981'
                            }}
                            title={movie.has_uploaded_video ? 'Replace uploaded video' : 'Upload movie video file'}
                            data-testid={`row-upload-video-${movie.movie_id}`}
                          >
                            <Video size={14} />
                            <span className="hidden sm:inline">
                              {rowUploading[movie.movie_id]
                                ? (rowPhase[movie.movie_id] === 'finalizing'
                                    ? 'Finalizing…'
                                    : `Uploading ${rowProgress[movie.movie_id] || 0}%`)
                                : (movie.has_uploaded_video ? 'Replace Video' : 'Upload Video')}
                            </span>
                            <input
                              ref={(el) => { if (el) rowFileInputRefs.current[movie.movie_id] = el; }}
                              type="file"
                              accept="video/mp4,video/webm,video/quicktime,video/x-m4v"
                              className="hidden"
                              disabled={rowUploading[movie.movie_id]}
                              onChange={(e) => handleRowVideoUpload(movie.movie_id, e.target.files[0])}
                            />
                          </label>
                        ) : (
                          <span
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold cursor-not-allowed"
                            style={{
                              backgroundColor: 'rgba(129, 151, 164, 0.10)',
                              color: '#8197A4',
                              border: '1px solid #2A3F52'
                            }}
                            title="Only master admins can upload or replace movie video files"
                            data-testid={`row-upload-video-locked-${movie.movie_id}`}
                          >
                            <Lock size={12} />
                            <span className="hidden sm:inline">Master only</span>
                          </span>
                        )}

                        <button onClick={() => { setEditMovie(movie); setShowForm(true); }}
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#3B82F6' }}
                          data-testid={`edit-movie-${movie.movie_id}`}
                          title="Edit">
                          <Edit size={16} />
                        </button>
                        <button onClick={() => handleDelete(movie.movie_id, movie.title)}
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: 'rgba(220, 38, 38, 0.15)', color: '#EF4444' }}
                          data-testid={`delete-movie-${movie.movie_id}`}
                          title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12">
              <Film size={40} className="mx-auto mb-3" style={{ color: '#8197A4' }} />
              <p style={{ color: '#8197A4' }}>{search ? 'No movies match your search' : 'No movies yet. Add your first movie!'}</p>
            </div>
          )}
        </div>
          </>
        )}

        {showForm && (
          <MovieForm
            movie={editMovie}
            onClose={() => { setShowForm(false); setEditMovie(null); }}
            onSaved={fetchMovies}
          />
        )}
      </div>
    </div>
  );
};
